import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { APP_VERSION } from "../appVersion";

const DEVICE_ID_KEY = "materialentnahme:device-id";
let volatileDeviceId: string | null = null;

export interface RegisteredDevice {
  id: number;
  user_id: number;
  username: string;
  device_id: string;
  device_name: string;
  platform: string | null;
  pwa_installed: boolean;
  is_active: boolean;
  app_version: string | null;
  first_seen_at: string;
  last_seen_at: string;
  push_active: boolean;
  passkey_count: number;
  last_push_at: string | null;
  last_push_status: string | null;
  user_agent: string | null;
  lifecycle_state: "active" | "inactive" | "stale";
  age_days: number;
  recent_days: number;
  stale_days: number;
}


function normalizeRegisteredDevice(device: RegisteredDevice): RegisteredDevice {
  const recentDays = Number.isFinite(device.recent_days) ? device.recent_days : 30;
  const staleDays = Number.isFinite(device.stale_days) ? device.stale_days : 90;
  const parsed = Date.parse(device.last_seen_at);
  const fallbackAge = Number.isFinite(parsed) ? Math.max(0, Math.floor((Date.now() - parsed) / 86_400_000)) : 0;
  const ageDays = Number.isFinite(device.age_days) ? device.age_days : fallbackAge;
  const lifecycleState = device.lifecycle_state === "active" || device.lifecycle_state === "inactive" || device.lifecycle_state === "stale"
    ? device.lifecycle_state
    : ageDays >= staleDays ? "stale" : ageDays >= recentDays ? "inactive" : "active";
  return { ...device, lifecycle_state: lifecycleState, age_days: ageDays, recent_days: recentDays, stale_days: staleDays };
}

function fallbackDeviceId(): string {
  try {
    const existing = sessionStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const next = volatileDeviceId ?? crypto.randomUUID();
    volatileDeviceId = next;
    sessionStorage.setItem(DEVICE_ID_KEY, next);
    return next;
  } catch {
    volatileDeviceId ??= crypto.randomUUID();
    return volatileDeviceId;
  }
}

export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const next = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, next);
    return next;
  } catch {
    return fallbackDeviceId();
  }
}

export function isInstalledPwa(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function currentDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? "Android-Smartphone" : "Android-Gerät";
  if (/Windows/i.test(ua)) return "Windows-PC";
  if (/Macintosh|Mac OS X/i.test(ua)) return "Mac";
  return "Browser-Gerät";
}

export async function registerCurrentDevice(): Promise<RegisteredDevice> {
  const response = await fetchWithTimeout("/api/devices/register", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      device_id: getDeviceId(),
      device_name: currentDeviceName(),
      platform: navigator.platform || null,
      user_agent: navigator.userAgent,
      pwa_installed: isInstalledPwa(),
      app_version: APP_VERSION,
    }),
  }, 10_000);
  if (!response.ok) throw new Error(`Gerät konnte nicht registriert werden (${response.status}).`);
  return normalizeRegisteredDevice(await response.json() as RegisteredDevice);
}

export async function fetchAdminDevices(): Promise<RegisteredDevice[]> {
  const response = await fetchWithTimeout("/api/admin/devices", { credentials: "include", cache: "no-store" }, 10_000);
  if (!response.ok) throw new Error(`Geräte konnten nicht geladen werden (${response.status}).`);
  return (await response.json() as RegisteredDevice[]).map(normalizeRegisteredDevice);
}

export interface DeviceRegistrySummary {
  total: number;
  active: number;
  inactive: number;
  stale: number;
  recent_days: number;
  stale_days: number;
}

export interface RegisteredDevicePage { items: RegisteredDevice[]; page:number; page_size:number; total:number; total_pages:number; summary:DeviceRegistrySummary; }

function summarizeDevices(devices: RegisteredDevice[]): DeviceRegistrySummary {
  const recentDays = devices[0]?.recent_days ?? 30;
  const staleDays = devices[0]?.stale_days ?? 90;
  return {
    total: devices.length,
    active: devices.filter((device) => device.lifecycle_state === "active").length,
    inactive: devices.filter((device) => device.lifecycle_state === "inactive").length,
    stale: devices.filter((device) => device.lifecycle_state === "stale").length,
    recent_days: recentDays,
    stale_days: staleDays,
  };
}
export async function fetchAdminDevicesPage(page=1, query=""): Promise<RegisteredDevicePage> {
  try {
    const response=await fetchWithTimeout(`/api/admin/devices/page?page=${page}&page_size=50&q=${encodeURIComponent(query)}`,{credentials:"include",cache:"no-store",headers:{Accept:"application/json"}},10_000);
    if(response.status===404||response.status===405||response.status>=500){
      const clean=query.trim().toLowerCase();
      const legacy=(await fetchAdminDevices()).filter((device)=>!clean||device.username.toLowerCase().includes(clean)||device.device_name.toLowerCase().includes(clean)||(device.platform??"").toLowerCase().includes(clean));
      const total=legacy.length,totalPages=total?Math.ceil(total/50):0,safePage=totalPages?Math.min(Math.max(1,page),totalPages):1,start=(safePage-1)*50;
      return {items:legacy.slice(start,start+50),page:safePage,page_size:50,total,total_pages:totalPages,summary:summarizeDevices(legacy)};
    }
    if(!response.ok) throw new Error("Geräte konnten nicht geladen werden. Prüfe die Verbindung und versuche es erneut.");
    const payload = await response.json() as RegisteredDevicePage;
    const items = payload.items.map(normalizeRegisteredDevice);
    return { ...payload, items, summary: payload.summary ?? summarizeDevices(items) };
  } catch(error) {
    if(error instanceof DOMException&&error.name==="AbortError") throw new Error("Die Geräteliste konnte innerhalb von 10 Sekunden nicht geladen werden. Prüfe die Verbindung und versuche es erneut.", { cause: error });
    throw error;
  }
}

export async function adminTestDevicePush(registrationId: number): Promise<void> {
  const response = await fetchWithTimeout(`/api/admin/devices/registrations/${registrationId}/test-push`, { method: "POST", credentials: "include" }, 10_000);
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { detail?: string };
    throw new Error(data.detail || `Test-Push fehlgeschlagen (${response.status}).`);
  }
}

export async function removeRegisteredDevice(registrationId: number): Promise<void> {
  const response = await fetchWithTimeout(`/api/admin/devices/registrations/${registrationId}`, { method: "DELETE", credentials: "include" }, 10_000);
  if (!response.ok) throw new Error(`Gerät konnte nicht entfernt werden (${response.status}).`);
}


export async function cleanupStaleRegisteredDevices(): Promise<{ removed: number; summary: DeviceRegistrySummary }> {
  const response = await fetchWithTimeout("/api/admin/devices/stale/cleanup", { method: "POST", credentials: "include" }, 10_000);
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { detail?: string };
    throw new Error(data.detail || `Veraltete Geräte konnten nicht bereinigt werden (${response.status}).`);
  }
  return response.json() as Promise<{ removed: number; summary: DeviceRegistrySummary }>;
}
